import type { ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'

import { useGsap } from '@/hooks/use-gsap'
import { Marquee } from '@/components/generals/marquee'
import { ProductPanel } from '@/components/commerce/product-panel'
import { productAssurances } from '@/constants/product'

/**
 * The home page's buy moment. The panel itself is shared with /shop, so this
 * component only owns the band, the entrance choreography and the assurances.
 */
export const OrderSection = (): ReactNode => {
    const scope = useGsap<HTMLElement>(({ gsap, select, scope: root, reduced }) => {
        if (reduced) return

        gsap.from(select('[data-order-gallery]'), {
            y: 56,
            opacity: 0,
            duration: 1.1,
            ease: 'nuvea',
            scrollTrigger: { trigger: root, start: 'top 72%' }
        })

        gsap.from(select('[data-order-detail]'), {
            y: 30,
            opacity: 0,
            duration: 0.9,
            stagger: 0.07,
            ease: 'nuvea',
            scrollTrigger: { trigger: root, start: 'top 68%' }
        })

        // The jar lifts a little as the section scrolls, so it never feels static.
        gsap.fromTo(
            select('[data-order-main] img'),
            { yPercent: 4 },
            {
                yPercent: -4,
                ease: 'none',
                scrollTrigger: {
                    trigger: select('[data-order-gallery]')[0],
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: 0.8
                }
            }
        )
    })

    return (
        <section
            ref={scope}
            id='ordernow'
            className='scroll-mt-20 border-y border-stone-line/70 bg-shell py-20 md:py-28'
        >
            <div className='shell-x mx-auto w-full max-w-[1760px]'>
                <ProductPanel />

                <p data-order-detail='' className='mt-10'>
                    <Link
                        href='/shop'
                        className='type-small group inline-flex items-center gap-2 rounded-full border border-stone-line px-5 py-3 text-ink transition-[background-color,border-color,color] duration-400 ease-nuvea hover:border-ink hover:bg-ink hover:text-cream'
                    >
                        See the full product page

                        <ArrowUpRight
                            className='size-4 transition-transform duration-400 ease-nuvea group-hover:translate-x-0.5 group-hover:-translate-y-0.5'
                            strokeWidth={1.6}
                        />
                    </Link>
                </p>
            </div>

            <div className='mt-16 border-t border-stone-line/70 pt-8 md:mt-24'>
                <Marquee duration={38}>
                    {productAssurances.map((item) => (
                        <span
                            key={item}
                            className='type-small flex items-center gap-8 px-8 whitespace-nowrap text-slate-soft'
                        >
                            {item}

                            <span aria-hidden='true' className='block size-1 rounded-full bg-stone-line' />
                        </span>
                    ))}
                </Marquee>
            </div>
        </section>
    )
}
